import React, { useState } from 'react'
import UserService from '@/utils/services/UserService'
import { toast } from 'react-hot-toast'
import { X, Users, AlertCircle } from 'lucide-react'

const DeviceOverrideModal = ({ user, onClose, onSuccess }) => {
  const [deviceCount, setDeviceCount] = useState(user.deviceOverride || user.currentDeviceCount + 1)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (deviceCount < user.currentDeviceCount) {
      toast.error('Device count cannot be less than current usage')
      return
    }

    if (deviceCount <= 0) {
      toast.error('Device count must be greater than 0')
      return
    }

    setLoading(true)
    try {
      await UserService.setDeviceOverride(user.uid, deviceCount)
      toast.success(`Device override set to ${deviceCount} devices`)
      onSuccess()
      onClose()
    } catch (error) {
      toast.error('Failed to set device override')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async () => {
    setLoading(true)
    try {
      await UserService.removeDeviceOverride(user.uid)
      toast.success('Device override removed')
      onSuccess()
      onClose()
    } catch (error) {
      toast.error('Failed to remove device override')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Device Override</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">User Information</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Name:</span> {user.displayName}</p>
              <p><span className="font-medium">Email:</span> {user.email}</p>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Current Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Current Usage:</span>
                <span className="font-medium text-blue-900">{user.currentDeviceCount} devices</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Subscription Limit:</span>
                <span className="font-medium text-blue-900">{user.subscriptionDeviceLimit} devices</span>
              </div>
              {user.deviceOverride && (
                <div className="flex justify-between">
                  <span className="text-blue-700">Current Override:</span>
                  <span className="font-medium text-green-600">{user.deviceOverride} devices</span>
                </div>
              )}
            </div>
          </div>

          {/* Override Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Device Limit
            </label>
            <input
              type="number"
              value={deviceCount}
              onChange={(e) => setDeviceCount(parseInt(e.target.value) || 0)}
              min={user.currentDeviceCount}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter device count"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum: {user.currentDeviceCount} devices (current usage)
            </p>
          </div>

          {/* Warning */}
          {deviceCount > (user.subscriptionDeviceLimit || 1) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Override Notice</p>
                  <p>This will grant the user more devices than their subscription allows.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          {user.deviceOverride && (
            <button
              onClick={handleRemove}
              disabled={loading}
              className="px-4 py-2 text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              Remove Override
            </button>
          )}
          
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Set Override'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeviceOverrideModal 